import { toast } from "@/components";
import { Checkbox, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, planStatus } from "@/lib/utils";
import { useAddPlanMutation, useGetPlanListByProjectId, useUpdatedPlanStatusMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Edit, RotateCcw } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { NAME } from "./consts";
import { schema } from "./validations";
import { useDispatch } from "react-redux";
import { setTaskData } from "@/redux/slices";

const useProjectPlan = () => {
  const { id } = useParams();
  const [globalFilter, setGlobalFilter] = useState("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openAddPlanDialog, setOpenAddPlanDialog] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<any>("");
  const [reason, setReason] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isRevised = useRef<boolean>(false);

  const navigate = useNavigate();

  const form = useForm<any>({
    defaultValues: {
      [NAME]: "",
    },
    resolver: zodResolver(schema),
  });
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: planList,
    isLoading: isPlanLoading,
  } = useGetPlanListByProjectId({ projectId: id as string });

  const isApprovePlanExist = useMemo(() => {
    if (planList?.data) {
      return planList?.data?.some((data: any) => data?.status == planStatus.approved.value)
    }
  }, [planList])

  // Define Table Columns
  const columns = [
    ...isApprovePlanExist ? [] : [{
      accessorKey: "projectPlanId",
      header: "",
      cell: (row: any) => (
        <Checkbox
          key={`plan-${row?.row?.original?.projectPlanId}`}
          checked={selectedPlan === row?.row?.original?.projectPlanId}
          onCheckedChange={(checked) => {
            if (checked === true) {
              setSelectedPlan(row?.row?.original?.projectPlanId);
            }
          }}
        />
      ),
      size: 100,
    }],
    {
      accessorKey: "projectPlanId",
      header: "ID",
      accessorFn: (row: any) => String(row.projectPlanId),
      size: 100,
    },
    {
      accessorKey: "name",
      header: "Plan Name",
      cell: ({ row }: any) => {
        return row.original.name ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(setTaskData(null));
              navigate(`${row.original.projectPlanId}/view`);
            }}
          >
            {String(row.original.name)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "version",
      header: "Version",
      accessorFn: (row: any) => String(row?.version || ""),
      size: 100,
    },
    {
      accessorKey: "taskCount",
      header: "# Task",
      accessorFn: (row: any) => String(row?.taskCount || ""),
      size: 100,
    },
    {
      accessorKey: "duration",
      header: "Duration (Days)",
      accessorFn: (row: any) => String(row?.duration || ""),
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Plan Status",
      cell: (row: any) => (
        <span
          className={cn(
            "font-[600]",
            planStatus[row?.row?.original?.status?.toLowerCase()]?.className
          )}
        >
          {planStatus[row?.row?.original?.status?.toLowerCase()]?.label ||
            ""}
        </span>
      ),
      size: 100,
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) => row?.createdAt ? moment(row?.createdAt).format('MM-DD-YYYY') : "",
      size: 100,
    },
    {
      accessorKey: "updatedAt",
      header: "Updated On",
      accessorFn: (row: any) => row?.updatedAt ? moment(row?.updatedAt).format('MM-DD-YYYY') : "",
      size: 100,
    },
    {
      accessorKey: "updatedByName",
      header: "Updated By",
      accessorFn: (row: any) => row?.updatedByName || "",
      size: 100,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            {row.row?.original?.status == planStatus.approved.value ? <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                isRevised.current = true;
                setSelectedPlan(row.row.original.projectPlanId);
                setShowConfirmation(true);
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Revise
            </DropdownMenuItem> : <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(setTaskData(null));
                navigate(
                  `${row.row?.original?.projectPlanId}/edit`
                );
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const { mutate: addPlan, isPending: isAddPlanPending } =
    useAddPlanMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["plan-list", id],
        });
        setOpenAddPlanDialog(false);
        toast({ message: "Plan Added Successfully" });
      },
    });
    
  const onAdd = () => {
    setOpenAddPlanDialog(true);
  };

  const onConfirmAddPlan = (data: any) => {
    addPlan({
      projectId: id as string,
      data: { ...data },
    });
  }

  const onSubmitApproval = () => {
    isRevised.current = false;
    setShowConfirmation(true);
  }

  const onChangeReason = (e: any) => {
    setReason(e.target.value)
  }

  const { mutate: updateStatus, isPending: isUpdatePending } = useUpdatedPlanStatusMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["plan-list", id],
      });
      setShowConfirmation(false);
      setReason("");
      setSelectedPlan("");
      toast({ message: "Plan submitted for approval." });
    },
  });

  const onConfirmSubmit = () => {
    if (reason?.length < 21) {
      toast({type: 'error', message: 'Minimum 20 character required for reason'});
      return;
    }

    updateStatus({
      planId: selectedPlan,
      projectId: id as string,
      data: {
        action: isRevised.current ? planStatus.revised.value : planStatus.approval_pending.value,
        comment: reason
      }
    })
  }

  return {
    globalFilter,
    setGlobalFilter,
    columns,
    onClose,
    openDialog,
    planList,
    isPlanLoading,
    isMobile,
    onAdd,
    selectedPlan,
    onSubmitApproval,
    showConfirmation,
    setShowConfirmation,
    reason,
    onChangeReason,
    onConfirmSubmit,
    isUpdatePending,
    isApprovePlanExist,
    openAddPlanDialog,
    setOpenAddPlanDialog,
    onConfirmAddPlan,
    form,
    isAddPlanPending,
    isRevised
  };
};

export default useProjectPlan;
